import axios from 'axios';
import { User, Group, Bill, GroupMember, SplitResult, SplitwiseGroup, MemberMapping } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
export const BASE_URL = API_URL.replace('/api', '');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  register: async (data: {
    email: string;
    password: string;
    name: string;
    phone?: string;
    defaultIsVegetarian?: boolean;
    defaultDrinksAlcohol?: boolean;
  }) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response = await api.get('/auth/profile');
    return response.data;
  },
};

// Groups API
export const groupsAPI = {
  create: async (data: {
    name: string;
    description?: string;
    members?: any[];
  }) => {
    const response = await api.post('/groups', data);
    return response.data;
  },

  getAll: async (): Promise<{ groups: Group[] }> => {
    const response = await api.get('/groups');
    return response.data;
  },

  getById: async (
    id: string
  ): Promise<{ group: Group; members: GroupMember[] }> => {
    const response = await api.get(`/groups/${id}`);
    return response.data;
  },

  addMember: async (
    groupId: string,
    data:
      | {
          userId: string;
          isVegetarian?: boolean;
          drinksAlcohol?: boolean;
        }
      | {
          guestName: string;
          guestEmail?: string;
          guestPhone?: string;
          isVegetarian?: boolean;
          drinksAlcohol?: boolean;
        }
  ) => {
    const response = await api.post(`/groups/${groupId}/members`, data);
    return response.data;
  },

  updateMemberPreferences: async (
    groupId: string,
    memberId: string,
    data: {
      isVegetarian?: boolean;
      drinksAlcohol?: boolean;
      guestName?: string;
    }
  ) => {
    const response = await api.put(
      `/groups/${groupId}/members/${memberId}`,
      data
    );
    return response.data;
  },

  removeMember: async (groupId: string, memberId: string) => {
    const response = await api.delete(`/groups/${groupId}/members/${memberId}`);
    return response.data;
  },

  getGroupBalances: async (groupId: string) => {
    const response = await api.get(`/groups/${groupId}/balances`);
    return response.data;
  },

  getAllBalances: async () => {
    const response = await api.get('/groups/balances');
    return response.data;
  },
};

// Bills API
export const billsAPI = {
  upload: async (formData: FormData) => {
    const response = await api.post('/bills', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getAll: async (groupId?: string): Promise<{ bills: Bill[] }> => {
    const params = groupId ? { groupId } : {};
    const response = await api.get('/bills', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/bills/${id}`);
    return response.data;
  },

  updateItem: async (
    billId: string,
    itemId: string,
    data: { category?: string; isShared?: boolean }
  ) => {
    const response = await api.put(`/bills/${billId}/items/${itemId}`, data);
    return response.data;
  },

  calculateSplit: async (billId: string): Promise<SplitResult> => {
    const response = await api.post(`/bills/${billId}/calculate`);
    return response.data;
  },

  finalize: async (billId: string) => {
    const response = await api.post(`/bills/${billId}/finalize`);
    return response.data;
  },

  delete: async (billId: string) => {
    const response = await api.delete(`/bills/${billId}`);
    return response.data;
  },
};

// Splitwise API
export const splitwiseAPI = {
  getAuthUrl: async (): Promise<{ url: string }> => {
    const response = await api.get('/splitwise/auth-url');
    return response.data;
  },

  handleCallback: async (code: string) => {
    const response = await api.post('/splitwise/callback', { code });
    return response.data;
  },

  getStatus: async (): Promise<{ connected: boolean }> => {
    const response = await api.get('/splitwise/status');
    return response.data;
  },

  disconnect: async () => {
    const response = await api.delete('/splitwise/disconnect');
    return response.data;
  },

  getGroups: async (): Promise<{ groups: SplitwiseGroup[] }> => {
    const response = await api.get('/splitwise/groups');
    return response.data;
  },

  syncBill: async (data: {
    billId: string;
    splitwiseGroupId: number;
    memberMappings: MemberMapping[];
    payerSplitwiseUserId: number;
  }) => {
    const response = await api.post('/splitwise/sync', data);
    return response.data;
  },

  getSyncStatus: async (billId: string): Promise<{ synced: boolean; sync: any }> => {
    const response = await api.get(`/splitwise/sync-status/${billId}`);
    return response.data;
  },
};

export default api;
